import { useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/lib/auth";
import { useSetPageHeader } from "@/lib/page-header";
import { agendaViewRange, type AgendaViewMode, type WeekAppointment } from "@/lib/dashboard-week";
import { resolveDashboardMode } from "@/lib/dashboard-mode";
import { useIsMobile } from "@/lib/use-media-query";
import type { IncomingOrderRow } from "@/components/production/production-types";
import { useOwnedOrganizations } from "@/lib/owned-organizations";
import { QuickActions } from "@/components/dashboard2/QuickActions";
import { InicioAgendaCard } from "@/components/dashboard2/InicioAgendaCard";
import { InicioAgendaMobile } from "@/components/dashboard2/InicioAgendaMobile";
import { LabDashboardStats } from "@/components/dashboard2/LabDashboardStats";
import { CompleteSeuLaboratorio } from "@/components/dashboard2/CompleteSeuLaboratorio";
import { LabDeadlineSummary } from "@/components/dashboard2/LabDeadlineSummary";
import { MyProductionList } from "@/components/dashboard2/MyProductionList";
import { OwnedOrdersDeadlineSummary } from "@/components/dashboard2/OwnedOrdersDeadlineSummary";
import { FiltroDeOrganizacoes, useOrganizacoesMarcadas, type OrganizacaoDoFiltro } from "@/components/shared/FiltroDeOrganizacoes";
import { ConsultaDeOutraClinica } from "@/components/dashboard2/ConsultaDeOutraClinica";
import { PendingOrgInvitesList } from "@/components/team/PendingOrgInvitesList";
import { AvisoDeEndereco } from "@/components/organization/AvisoDeEndereco";
import { usePrazoDoLaboratorio } from "@/lib/prazo-do-laboratorio";

export function Inicio() {
  const { user } = useAuth();
  const { data: me } = trpc.me.current.useQuery();
  const firstName = me?.name?.split(" ")[0] ?? "";

  useSetPageHeader(
    {
      title: "Início",
      subtitle: firstName ? `Bem-vindo(a) de volta, ${firstName}` : undefined,
    },
    [firstName],
  );

  const isPersonalOrg = !!user?.activeOrganization?.isPersonal;
  const { clinics: ownedClinics, labs: ownedLabs } = useOwnedOrganizations();
  const activeOrgId = user?.activeOrganization?.id;
  const otherOwnedClinics = useMemo(() => ownedClinics.filter((o) => o.id !== activeOrgId), [ownedClinics, activeOrgId]);
  const otherOwnedLabs = useMemo(() => ownedLabs.filter((o) => o.id !== activeOrgId), [ownedLabs, activeOrgId]);
  const showOwnedLabsSummary = isPersonalOrg && otherOwnedLabs.length > 0;

  const membershipsQuery = trpc.organization.listMine.useQuery();
  const otherMemberships = useMemo(
    () => (membershipsQuery.data ?? []).filter((m) => m.organization.id !== activeOrgId),
    [membershipsQuery.data, activeOrgId],
  );
  const showScopeFilter = isPersonalOrg && (otherOwnedClinics.length > 0 || otherMemberships.length > 0);
  // Mesmo filtro (e a mesma escolha guardada) da página Agenda.
  const organizacoesDoFiltro = useMemo<OrganizacaoDoFiltro[]>(
    () => [
      ...(activeOrgId ? [{ id: activeOrgId, nome: "Pessoal", pessoal: true }] : []),
      ...otherMemberships
        .filter((m) => !m.organization.types?.length || m.organization.types.includes("DENTAL_OFFICE"))
        .map((m) => ({ id: m.organization.id, nome: m.organization.name })),
    ],
    [activeOrgId, otherMemberships],
  );
  const filtroDeOrganizacoes = useOrganizacoesMarcadas("agenda:filtro-de-organizacoes", organizacoesDoFiltro);
  const marcadas = filtroDeOrganizacoes.marcadas;

  const canViewAllProduction = me?.activeOrganization?.canViewAllProduction ?? true;

  const types = user?.activeOrganization?.types ?? [];
  const mode = resolveDashboardMode(types);
  const showOffice = mode === "office" || mode === "hybrid";
  const showLab = mode === "lab" || mode === "hybrid";

  const isMobile = useIsMobile();
  const [viewMode, setViewMode] = useState<AgendaViewMode>(() => (isMobile ? "day" : "month"));

  // --- Clínica: agenda + sinal secundário de pedidos enviados ---
  const agendaRange = agendaViewRange(viewMode);
  const rangeQueryArgs = { rangeStart: agendaRange.start, rangeEnd: agendaRange.end };
  const agendaQuery = trpc.appointment.listByRange.useQuery(rangeQueryArgs, { enabled: showOffice });

  // --- Laboratório: pedidos recebidos + SLA configurado no perfil ---
  const incomingQuery = trpc.prosthesis.incoming.list.useQuery(undefined, { enabled: showLab });
  const incomingOrders = (incomingQuery.data ?? []) as IncomingOrderRow[];
  const prazo = usePrazoDoLaboratorio();

  // --- Conta Pessoal: compromissos de OUTRAS organizações mesclados na mesma agenda (mesmo
  // padrão de /agenda) — `listOwnedAcrossOrgs` (OWNER) cobre as organizações possuídas,
  // `listMyAppointmentsAcrossOrgs` (qualquer vínculo) cobre onde só é membro; dedup evita
  // mostrar 2x o que já vem pela query de OWNER.
  // Consulta de outra clínica clicada na agenda da Pessoal: abre o diálogo dela, agindo lá.
  const [consultaDeFora, setConsultaDeFora] = useState<WeekAppointment | null>(null);
  const needsForeignData = showScopeFilter && organizacoesDoFiltro.some((o) => !o.pessoal && marcadas.has(o.id));
  const ownedAcrossOrgsQuery = trpc.appointment.listOwnedAcrossOrgs.useQuery(rangeQueryArgs, {
    enabled: needsForeignData && otherOwnedClinics.length > 0,
  });
  const myAppointmentsAcrossOrgsQuery = trpc.appointment.listMyAppointmentsAcrossOrgs.useQuery(rangeQueryArgs, {
    enabled: needsForeignData && otherMemberships.length > 0,
  });
  const foreignAppts = useMemo<WeekAppointment[]>(() => {
    if (!needsForeignData) return [];
    const ownedOrgIds = new Set(otherOwnedClinics.map((o) => o.id));
    const owned = (ownedAcrossOrgsQuery.data ?? []) as any[];
    const mine = ((myAppointmentsAcrossOrgsQuery.data ?? []) as any[]).filter(
      (a) => !ownedOrgIds.has(a.organization.id),
    );
    return [...owned, ...mine].map((a) => ({
      id: a.id,
      patientId: a.patientId,
      patientName: a.patientName,
      patientPhone: a.patientPhone,
      dentistName: a.dentistName,
      dentistId: a.dentistId,
      sala: a.sala,
      notes: a.notes,
      createdAt: a.createdAt,
      startedAt: a.startedAt,
      finishedAt: a.finishedAt,
      date: a.date,
      startTime: a.startTime,
      endTime: a.endTime,
      status: a.status,
      color: a.color,
      procedure: a.procedure,
      patient: a.patient,
      ehDeOutraOrganizacao: true,
      organizationId: a.organization.id,
      organizationName: a.organization.name,
    }));
  }, [needsForeignData, otherOwnedClinics, ownedAcrossOrgsQuery.data, myAppointmentsAcrossOrgsQuery.data]);

  const includeOwn = !showScopeFilter || marcadas.has(activeOrgId ?? "");

  /** Compromissos de outras organizações já filtrados — desktop e celular consomem esta mesma lista. */
  const foreignVisiveis = useMemo(() => foreignAppts.filter((a) => a.organizationId && marcadas.has(a.organizationId)), [foreignAppts, marcadas]);

  const agendaAppts = useMemo(() => {
    const own = includeOwn ? ((agendaQuery.data ?? []) as WeekAppointment[]) : [];
    return [...own, ...foreignVisiveis];
  }, [includeOwn, agendaQuery.data, foreignVisiveis]);

  const agendaLoading =
    agendaQuery.isLoading || (needsForeignData && (ownedAcrossOrgsQuery.isLoading || myAppointmentsAcrossOrgsQuery.isLoading));

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-6">
      <AvisoDeEndereco />
      <PendingOrgInvitesList />

      {showOffice && (
        <div className="space-y-6">
          {mode === "hybrid" && (
            <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">
              Clínica
            </h2>
          )}
          <QuickActions />
          {showScopeFilter && (
            <FiltroDeOrganizacoes
              organizacoes={organizacoesDoFiltro}
              {...filtroDeOrganizacoes.controle}
            />
          )}
          {isMobile ? (
            // No celular a agenda do Início é a MESMA da página /agenda (grade real, borda a
            // borda), com os controles acima — o card compacto fica só no desktop.
            <InicioAgendaMobile
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              extraAppointments={foreignVisiveis}
              includeOwn={includeOwn}
              onSelectForeign={(a) => a.organizationId && setConsultaDeFora(a)}
            />
          ) : (
          <InicioAgendaCard
            appts={agendaAppts}
            loading={agendaLoading}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            onSelectForeign={(a) => a.organizationId && setConsultaDeFora(a)}
          />
          )}
        </div>
      )}

      {showLab && (
        <div className="space-y-6">
          {mode === "hybrid" && (
            <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">
              Laboratório
            </h2>
          )}
          <CompleteSeuLaboratorio />
          <LabDashboardStats prazo={prazo} />
          {canViewAllProduction ? (
            <LabDeadlineSummary
              orders={incomingOrders}
              loading={incomingQuery.isLoading}
              prazo={prazo}
            />
          ) : (
            <MyProductionList orders={incomingOrders} loading={incomingQuery.isLoading} />
          )}
        </div>
      )}

      {showOwnedLabsSummary && (
        <div className="space-y-6">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">
            Outras organizações
          </h2>
          <OwnedOrdersDeadlineSummary />
        </div>
      )}

      <ConsultaDeOutraClinica consulta={consultaDeFora} onClose={() => setConsultaDeFora(null)} />
    </div>
  );
}
